import React, { useState, useEffect } from 'react';
import {
  Equipment,
  CreateEquipmentDto,
  UpdateEquipmentDto,
  EquipmentType,
  EquipmentStatus,
  EquipmentCondition,
  MaintenanceFrequency,
  getEquipmentTypeLabel,
  getEquipmentStatusLabel,
  getEquipmentConditionLabel,
  getMaintenanceFrequencyLabel,
} from '../api/equipment';
import { suppliersApi, Supplier } from '../api/suppliers';
import AmountInput from './ui/AmountInput';

interface EquipmentFormProps {
  equipment?: Equipment;
  onSubmit: (data: CreateEquipmentDto | UpdateEquipmentDto) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const EquipmentForm: React.FC<EquipmentFormProps> = ({
  equipment,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateEquipmentDto>({
    name: '',
    type: EquipmentType.OTHER,
    purchasePrice: 0,
    depreciationRate: 10,
    usefulLifeYears: 5,
    maintenanceFrequency: MaintenanceFrequency.MONTHLY,
    inUse: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // Load suppliers on component mount
  useEffect(() => {
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const activeSuppliers = await suppliersApi.getActiveSuppliers();
        setSuppliers(activeSuppliers);
      } catch (error) {
        console.error('Failed to load suppliers:', error);
      } finally {
        setLoadingSuppliers(false);
      }
    };

    loadSuppliers();
  }, []);

  // Initialize form data when equipment prop changes
  useEffect(() => {
    if (equipment) {
      setFormData({
        name: equipment.name,
        type: equipment.type,
        model: equipment.model || '',
        serialNumber: equipment.serialNumber || '',
        assetTag: equipment.assetTag || '',
        status: equipment.status,
        condition: equipment.condition,
        location: equipment.location || '',
        description: equipment.description || '',
        manufacturer: equipment.manufacturer || '',
        purchaseDate: equipment.purchaseDate ? new Date(equipment.purchaseDate) : undefined,
        purchasePrice: equipment.purchasePrice,
        currentValue: equipment.currentValue || undefined,
        depreciationRate: equipment.depreciationRate,
        usefulLifeYears: equipment.usefulLifeYears,
        warrantyExpiration: equipment.warrantyExpiration ? new Date(equipment.warrantyExpiration) : undefined,
        supplierId: equipment.supplierId || '',
        supplier: equipment.supplier || '',
        maintenanceFrequency: equipment.maintenanceFrequency,
        lastMaintenanceDate: equipment.lastMaintenanceDate ? new Date(equipment.lastMaintenanceDate) : undefined,
        nextMaintenanceDate: equipment.nextMaintenanceDate ? new Date(equipment.nextMaintenanceDate) : undefined,
        maintenanceInstructions: equipment.maintenanceInstructions || '',
        operatingInstructions: equipment.operatingInstructions || '',
        safetyNotes: equipment.safetyNotes || '',
        capacity: equipment.capacity || '',
        dimensions: equipment.dimensions || '',
        weight: equipment.weight || undefined,
        powerRequirements: equipment.powerRequirements || '',
        inUse: equipment.inUse,
        qrCode: equipment.qrCode || '',
        photos: equipment.photos || [],
        notes: equipment.notes || '',
      });
    }
  }, [equipment]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Equipment name is required';
    }

    if (!formData.type) {
      newErrors.type = 'Equipment type is required';
    }

    if (formData.purchasePrice < 0) {
      newErrors.purchasePrice = 'Purchase price must be positive';
    }

    if (formData.depreciationRate < 0 || formData.depreciationRate > 100) {
      newErrors.depreciationRate = 'Depreciation rate must be between 0 and 100';
    }

    if (formData.usefulLifeYears < 0) {
      newErrors.usefulLifeYears = 'Useful life must be positive';
    }

    if (formData.weight && formData.weight < 0) {
      newErrors.weight = 'Weight must be positive';
    }

    if (formData.currentValue && formData.currentValue < 0) {
      newErrors.currentValue = 'Current value must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleInputChange = (field: keyof CreateEquipmentDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatDateForInput = (date?: Date): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const parseDateFromInput = (dateString: string): Date | undefined => {
    if (!dateString) return undefined;
    return new Date(dateString);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Basic Information */}
        <div className='space-y-4'>
          <h3 className='text-lg font-medium text-slate-900'>Basic Information</h3>
          
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Equipment Name *
            </label>
            <input
              type='text'
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder='Enter equipment name'
            />
            {errors.name && <p className='text-red-500 text-sm mt-1'>{errors.name}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value as EquipmentType)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.type ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {Object.values(EquipmentType).map(type => (
                <option key={type} value={type}>
                  {getEquipmentTypeLabel(type)}
                </option>
              ))}
            </select>
            {errors.type && <p className='text-red-500 text-sm mt-1'>{errors.type}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Model/Brand
            </label>
            <input
              type='text'
              value={formData.model || ''}
              onChange={(e) => handleInputChange('model', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter model or brand'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Serial Number
            </label>
            <input
              type='text'
              value={formData.serialNumber || ''}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter serial number'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Asset Tag
            </label>
            <input
              type='text'
              value={formData.assetTag || ''}
              onChange={(e) => handleInputChange('assetTag', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter asset tag'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Manufacturer
            </label>
            <input
              type='text'
              value={formData.manufacturer || ''}
              onChange={(e) => handleInputChange('manufacturer', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter manufacturer'
            />
          </div>
        </div>

        {/* Status and Condition */}
        <div className='space-y-4'>
          <h3 className='text-lg font-medium text-slate-900'>Status & Condition</h3>
          
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Status
            </label>
            <select
              value={formData.status || EquipmentStatus.ACTIVE}
              onChange={(e) => handleInputChange('status', e.target.value as EquipmentStatus)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              {Object.values(EquipmentStatus).map(status => (
                <option key={status} value={status}>
                  {getEquipmentStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Condition
            </label>
            <select
              value={formData.condition || EquipmentCondition.GOOD}
              onChange={(e) => handleInputChange('condition', e.target.value as EquipmentCondition)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              {Object.values(EquipmentCondition).map(condition => (
                <option key={condition} value={condition}>
                  {getEquipmentConditionLabel(condition)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Location
            </label>
            <input
              type='text'
              value={formData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter location'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Supplier
            </label>
            <select
              value={formData.supplierId || ''}
              onChange={(e) => {
                const selectedSupplierId = e.target.value;
                const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
                handleInputChange('supplierId', selectedSupplierId);
                handleInputChange('supplier', selectedSupplier?.name || '');
              }}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              disabled={loadingSuppliers}
            >
              <option value=''>
                {loadingSuppliers ? 'Loading suppliers...' : 'Select a supplier (optional)'}
              </option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name} ({supplier.code})
                </option>
              ))}
            </select>
            {loadingSuppliers && (
              <p className='text-sm text-slate-500 mt-1'>Loading suppliers...</p>
            )}
          </div>

          <div className='flex items-center'>
            <input
              type='checkbox'
              id='inUse'
              checked={formData.inUse}
              onChange={(e) => handleInputChange('inUse', e.target.checked)}
              className='h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded'
            />
            <label htmlFor='inUse' className='ml-2 block text-sm text-slate-900'>
              Currently in use
            </label>
          </div>
        </div>
      </div>

      {/* Financial Information */}
      <div className='space-y-4'>
        <h3 className='text-lg font-medium text-slate-900'>Financial Information</h3>
        
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Purchase Price (ZAR) *
            </label>
            <AmountInput
              value={formData.purchasePrice}
              onChange={(v) => handleInputChange('purchasePrice', v ?? 0)}
              min={0}
              inputClassName={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.purchasePrice ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.purchasePrice && <p className='text-red-500 text-sm mt-1'>{errors.purchasePrice}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Current Value (ZAR)
            </label>
            <AmountInput
              value={formData.currentValue}
              onChange={(v) => handleInputChange('currentValue', v)}
              min={0}
              allowEmpty
              inputClassName={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.currentValue ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.currentValue && <p className='text-red-500 text-sm mt-1'>{errors.currentValue}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Depreciation Rate (% per year)
            </label>
            <input
              type='number'
              step='0.1'
              min='0'
              max='100'
              value={formData.depreciationRate}
              onChange={(e) => handleInputChange('depreciationRate', parseFloat(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.depreciationRate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.depreciationRate && <p className='text-red-500 text-sm mt-1'>{errors.depreciationRate}</p>}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Useful Life (years)
            </label>
            <input
              type='number'
              min='0'
              value={formData.usefulLifeYears}
              onChange={(e) => handleInputChange('usefulLifeYears', parseInt(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.usefulLifeYears ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.usefulLifeYears && <p className='text-red-500 text-sm mt-1'>{errors.usefulLifeYears}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Purchase Date
            </label>
            <input
              type='date'
              value={formatDateForInput(formData.purchaseDate)}
              onChange={(e) => handleInputChange('purchaseDate', parseDateFromInput(e.target.value))}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Warranty Expiration
            </label>
            <input
              type='date'
              value={formatDateForInput(formData.warrantyExpiration)}
              onChange={(e) => handleInputChange('warrantyExpiration', parseDateFromInput(e.target.value))}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>
        </div>
      </div>

      {/* Maintenance Information */}
      <div className='space-y-4'>
        <h3 className='text-lg font-medium text-slate-900'>Maintenance Information</h3>
        
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Maintenance Frequency
            </label>
            <select
              value={formData.maintenanceFrequency}
              onChange={(e) => handleInputChange('maintenanceFrequency', e.target.value as MaintenanceFrequency)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              {Object.values(MaintenanceFrequency).map(frequency => (
                <option key={frequency} value={frequency}>
                  {getMaintenanceFrequencyLabel(frequency)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Last Maintenance Date
            </label>
            <input
              type='date'
              value={formatDateForInput(formData.lastMaintenanceDate)}
              onChange={(e) => handleInputChange('lastMaintenanceDate', parseDateFromInput(e.target.value))}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Next Maintenance Date
            </label>
            <input
              type='date'
              value={formatDateForInput(formData.nextMaintenanceDate)}
              onChange={(e) => handleInputChange('nextMaintenanceDate', parseDateFromInput(e.target.value))}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>
        </div>
      </div>

      {/* Specifications */}
      <div className='space-y-4'>
        <h3 className='text-lg font-medium text-slate-900'>Specifications</h3>
        
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Capacity
            </label>
            <input
              type='text'
              value={formData.capacity || ''}
              onChange={(e) => handleInputChange('capacity', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='e.g., 50 birds, 100L'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Dimensions (L x W x H)
            </label>
            <input
              type='text'
              value={formData.dimensions || ''}
              onChange={(e) => handleInputChange('dimensions', e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='e.g., 120cm x 60cm x 180cm'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Weight (kg)
            </label>
            <input
              type='number'
              step='0.1'
              min='0'
              value={formData.weight || ''}
              onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || undefined)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.weight ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.weight && <p className='text-red-500 text-sm mt-1'>{errors.weight}</p>}
          </div>
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            Power Requirements
          </label>
          <input
            type='text'
            value={formData.powerRequirements || ''}
            onChange={(e) => handleInputChange('powerRequirements', e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='e.g., 220V AC, 50W'
          />
        </div>
      </div>

      {/* Additional Information */}
      <div className='space-y-4'>
        <h3 className='text-lg font-medium text-slate-900'>Additional Information</h3>
        
        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            Description
          </label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Enter equipment description'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            Maintenance Instructions
          </label>
          <textarea
            value={formData.maintenanceInstructions || ''}
            onChange={(e) => handleInputChange('maintenanceInstructions', e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Enter maintenance instructions'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            Operating Instructions
          </label>
          <textarea
            value={formData.operatingInstructions || ''}
            onChange={(e) => handleInputChange('operatingInstructions', e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Enter operating instructions'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            Safety Notes
          </label>
          <textarea
            value={formData.safetyNotes || ''}
            onChange={(e) => handleInputChange('safetyNotes', e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Enter safety notes and precautions'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            QR Code
          </label>
          <input
            type='text'
            value={formData.qrCode || ''}
            onChange={(e) => handleInputChange('qrCode', e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Enter QR code'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>
            Notes
          </label>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Enter additional notes'
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className='flex justify-end space-x-3 pt-6 border-t border-gray-200'>
        <button
          type='button'
          onClick={onCancel}
          className='px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type='submit'
          className='px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          disabled={loading}
        >
          {loading ? 'Saving...' : equipment ? 'Update Equipment' : 'Create Equipment'}
        </button>
      </div>
    </form>
  );
};

export default EquipmentForm; 